import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  LayoutDashboard,
  ListTree,
  FileSearch,
  BarChart2,
  Brain,
  Settings,
  AlertCircle,
  Plus,
  CircleDollarSign,
  Receipt,
  Globe,
} from 'lucide-react';
import api from '../api/client';

export default function CommandPalette({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [results, setResults] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const staticActions = useMemo(
    () => [
      {
        id: 'nav-dashboard',
        type: 'navigation',
        label: 'Go to Dashboard',
        icon: <LayoutDashboard size={16} />,
        action: () => navigate('/dashboard'),
      },
      {
        id: 'nav-graph',
        type: 'navigation',
        label: 'Exception Flow Map',
        icon: <ListTree size={16} />,
        action: () => navigate('/graph'),
      },
      {
        id: 'nav-search',
        type: 'navigation',
        label: 'Exception Logs',
        icon: <FileSearch size={16} />,
        action: () => navigate('/search'),
      },
      {
        id: 'nav-reports',
        type: 'navigation',
        label: 'Reports',
        icon: <BarChart2 size={16} />,
        action: () => navigate('/reports'),
      },
      {
        id: 'nav-assistant',
        type: 'navigation',
        label: 'Open Agent',
        icon: <Brain size={16} />,
        action: () => navigate('/agent', { state: { newChat: true } }),
      },
      {
        id: 'nav-settings',
        type: 'navigation',
        label: 'Settings',
        icon: <Settings size={16} />,
        action: () => navigate('/settings'),
      },
      {
        id: 'nav-billing',
        type: 'navigation',
        label: 'Plan & usage',
        icon: <Receipt size={16} />,
        action: () => navigate('/settings#billing'),
      },
      {
        id: 'nav-uptime',
        type: 'navigation',
        label: 'Ping & Uptime Monitor',
        icon: <Globe size={16} />,
        action: () => navigate('/uptime'),
      },
      {
        id: 'nav-pricing',
        type: 'navigation',
        label: 'Plans & pricing',
        icon: <CircleDollarSign size={16} />,
        action: () => navigate('/pricing'),
      },
      {
        id: 'new-chat',
        type: 'action',
        label: 'New chat',
        icon: <Plus size={16} />,
        action: () => navigate('/agent', { state: { newChat: true } }),
      },
    ],
    [navigate]
  );

  const runSearch = useCallback(
    async (q) => {
      if (!q.trim()) {
        setSearchResults([]);
        return;
      }
      try {
        const res = await api.get('/api/exceptions', {
          params: { search: q.trim(), limit: 5 },
        });
        const exceptions = (res.data.exceptions || []).map((e) => ({
          id: `exc-${e.id}`,
          type: 'exception',
          label: `${e.exception_type || 'Exception'} — ${e.service_name || 'unknown'}`,
          sublabel: (e.message || '').slice(0, 80),
          icon: <AlertCircle size={16} />,
          action: () => navigate(`/exceptions/${e.id}`),
        }));
        setSearchResults(exceptions);
      } catch {
        setSearchResults([]);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelected(0);
      setSearchResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    const q = (query || '').trim().toLowerCase();
    const filtered = q
      ? staticActions.filter((a) => a.label.toLowerCase().includes(q))
      : staticActions;
    setResults(filtered);

    const timer = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, runSearch, staticActions]);

  const allResults = [...results, ...searchResults];

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter' && allResults[selected]) {
      e.preventDefault();
      allResults[selected].action();
      onClose();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="command-backdrop" onClick={onClose} role="presentation" aria-hidden="true" />
      <div className="command-palette" role="dialog" aria-label="Command palette">
        <div className="command-input-wrap">
          <Search size={16} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search exceptions, navigate, or type a command..."
            className="command-input"
            autoFocus
          />
        </div>
        <div className="command-results">
          {allResults.length === 0 ? (
            <div className="command-empty">No results</div>
          ) : (
            allResults.map((item, i) => (
              <div
                key={item.id}
                role="button"
                tabIndex={-1}
                className={`command-item ${i === selected ? 'selected' : ''}`}
                onClick={() => {
                  item.action();
                  onClose();
                }}
                onMouseEnter={() => setSelected(i)}
              >
                <span className="command-item-icon">{item.icon}</span>
                <div className="command-item-text">
                  <span className="command-item-label">{item.label}</span>
                  {item.sublabel && <span className="command-item-sublabel">{item.sublabel}</span>}
                </div>
                <span className="command-item-type">{item.type}</span>
              </div>
            ))
          )}
        </div>
        <div className="command-footer">
          <span>Up/Down Navigate</span>
          <span>Enter Select</span>
          <span>Esc Close</span>
        </div>
      </div>
    </>
  );
}
